<script setup lang="ts">
import {
  ExclamationCircleIcon,
  ExclamationTriangleIcon,
  InformationCircleIcon,
} from "@heroicons/vue/24/outline";
import { computed, ref, watch } from "vue";
import { Collapse } from "vue-collapsed";
import { AlertType } from "@/enums/alertTypes";
import scrollToTarget from "@/helpers/scrollToTarget";

interface Props {
  type: AlertType;
  alerts: string[];
  title?: string;
  showLimit?: number;
  subTitle?: string;
  footerMessage?: string;
  bordered?: boolean;
  showNav?: boolean;
  remainingText?: string;
  removeOnOk?: boolean;
  automaticScroll?: boolean;
  onOk?: () => unknown;
}

const props = withDefaults(defineProps<Props>(), {
  title: "",
  remainingText: "más.",
  bordered: true,
  showNav: true,
  automaticScroll: false,
});
const hideShowMore = ref<boolean>(
  props.showLimit === undefined || props.alerts.length <= props.showLimit
);
const showMore = ref(hideShowMore.value);
const understood = ref(false);
const containerRef = ref(null);

const configurations = {
  Warning: {
    icon: ExclamationCircleIcon,
    color: "yellow",
  },
  Error: {
    icon: ExclamationTriangleIcon,
    color: "red",
  },
  Info: {
    icon: InformationCircleIcon,
    color: "blue",
  },
};

const icon = configurations[props.type].icon;

const styles = {
  background: `bg-${configurations[props.type].color}-50`,
  icon: `text-${configurations[props.type].color}-400`,
  title: `text-${configurations[props.type].color}-800`,
  text: `text-${configurations[props.type].color}-700`,
  border: `border-${configurations[props.type].color}-400`,
  button: `border-${configurations[props.type].color}-400`,
  buttonBackground: `bg-${configurations[props.type].color}-100`,
};

const transformedAlerts = computed(() => {
  if (
    props.showLimit &&
    props.alerts.length > props.showLimit &&
    !showMore.value
  ) {
    return props.alerts.slice(0, props.showLimit);
  }

  return props.alerts;
});

const expandable = computed(() => {
  return understood.value && props.alerts.length > 0;
});

const onOkHandler = () => {
  understood.value = true;
  props.onOk?.();
};

watch(understood,  (value) => {
  if (!value && props.automaticScroll) {
    scrollToTarget(containerRef.value!);
  }
})
</script>

<template>
  <div
    v-motion
    :initial="{
      opacity: 0,
    }"
    :enter="{
      opacity: 1,
      transition: {
        delay: 200
      },
    }"
    v-if="removeOnOk ? !understood : true"
    :class="[
      'rounded-md p-4 border',
      styles.background,
      bordered ? styles.border : 'border-transparent',
    ]"
    ref="containerRef"
  >
    
    <div class="flex">
      <div class="flex-shrink-0">
        <component :is="icon" :class="['h-5 w-5', styles.icon]" />
      </div>
      <div class="ml-3 flex flex-col gap-2">
        <h3
          v-if="!$slots['title']"
          :class="[
            'text-sm font-medium',
            styles.title,
            expandable && 'cursor-pointer',
          ]"
          @click="expandable ? (understood = !understood) : null"
        >
          {{ title }}
        </h3>

        <div
          v-else
          :class="[expandable && 'cursor-pointer']"
          @click="expandable ? (understood = !understood) : null"
        >
          <slot name="title" />
        </div>

        <Collapse :when="!understood" as="div" class="flex flex-col gap-2">
          <p v-if="subTitle" class="text-sm">{{ subTitle }}</p>

          <div :class="['text-sm flex flex-col gap-4', styles.text]">
            <ul
              role="list"
              class="list-disc pl-5 grid grid-cols-1 gap-x-2 gap-y-1 lg:grid-cols-2"
            >
              <li v-for="(alert, index) in transformedAlerts" :key="index">
                {{ alert }}
              </li>

              <li
                v-if="!hideShowMore && !showMore"
                class="underline underline-offset-4 select-none"
              >
                {{ `${alerts.length - showLimit!} ${remainingText}` }}
              </li>
            </ul>
          </div>

          <p v-if="footerMessage">{{ footerMessage }}</p>

          <nav v-if="showNav" class="flex items-center text-sm gap-4 mt-2">
            <button
              :class="[
                'p-1.5 px-3 rounded-md border hover:border-gray-400 fade-transition',
                styles.button,
                styles.buttonBackground,
              ]"
              @click.prevent="onOkHandler"
            >
              Entendido
            </button>

            <button
              v-if="!hideShowMore"
              :class="[
                'p-1.5 px-3 rounded-md border hover:border-gray-400 fade-transition',
                styles.button,
                styles.buttonBackground,
              ]"
              @click.prevent="showMore = !showMore"
            >
              {{ showMore ? "Mostrar menos" : "Mostrar más" }}
            </button>
          </nav>
        </Collapse>
      </div>
    </div>
  </div>
</template>

<style scoped></style>
